import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Installation, Company } from 'src/types';

interface InstallationFormProps {
  initialData?: Installation;
  companies: Company[];
  onSubmit: (data: Partial<Installation>) => void;
  isLoading: boolean;
  onCancel: () => void;
}

const InstallationForm = ({
  initialData,
  companies,
  onSubmit,
  isLoading,
  onCancel,
}: InstallationFormProps) => {
  const { t } = useTranslation();

  const [name, setName] = useState(initialData?.name ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [companyId, setCompanyId] = useState(initialData?.company_id ?? '');

  // Location
  const [address, setAddress] = useState(initialData?.location?.address ?? '');
  const [latitude, setLatitude] = useState(initialData?.location?.latitude?.toString() ?? '');
  const [longitude, setLongitude] = useState(initialData?.location?.longitude?.toString() ?? '');

  // Configuration
  const [leakageThreshold, setLeakageThreshold] = useState(
    initialData?.configuration?.leakage_threshold?.toString() ?? ''
  );
  // Deadman threshold is stored as an integer number of HOURS. Present it as a
  // value + unit (hours/days): if the stored hours are a whole number of days,
  // default the unit to days for readability, else hours.
  const initialDeadmanHours = initialData?.configuration?.deadman_threshold ?? null;
  const initialDeadmanIsDays =
    initialDeadmanHours != null && initialDeadmanHours % 24 === 0 && initialDeadmanHours !== 0;
  const [deadmanValue, setDeadmanValue] = useState(
    initialDeadmanHours == null
      ? ''
      : String(initialDeadmanIsDays ? initialDeadmanHours / 24 : initialDeadmanHours)
  );
  const [deadmanUnit, setDeadmanUnit] = useState<'hours' | 'days'>(
    initialDeadmanIsDays ? 'days' : 'hours'
  );

  // Pipeline Configuration
  const [gridStep, setGridStep] = useState(initialData?.configuration?.grid_step?.toString() ?? '');
  const [analyticsHistoryDays, setAnalyticsHistoryDays] = useState(
    initialData?.configuration?.analytics_history_days?.toString() ?? ''
  );
  const [influxInstallationName, setInfluxInstallationName] = useState(
    initialData?.configuration?.influx_installation_name ?? ''
  );
  const [defaultReferenceElectrode, setDefaultReferenceElectrode] = useState(
    initialData?.configuration?.default_reference_electrode ?? ''
  );
  const [voltageComboFilter, setVoltageComboFilter] = useState(
    initialData?.configuration?.voltage_combo_filter ?? ''
  );
  const [colorScaleMin, setColorScaleMin] = useState(
    initialData?.configuration?.color_scale_min != null
      ? String(initialData.configuration.color_scale_min)
      : ''
  );
  const [colorScaleMax, setColorScaleMax] = useState(
    initialData?.configuration?.color_scale_max != null
      ? String(initialData.configuration.color_scale_max)
      : ''
  );

  // Metadata
  const [monitoredArea, setMonitoredArea] = useState(
    initialData?.installation_metadata?.monitored_area?.toString() ?? ''
  );
  const [dateStartOperation, setDateStartOperation] = useState(
    initialData?.installation_metadata?.date_start_operation ?? ''
  );
  const [measuringSystem, setMeasuringSystem] = useState(
    initialData?.installation_metadata?.measuring_system ?? ''
  );
  const [brand, setBrand] = useState(initialData?.brand ?? 'protectum');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const data: Partial<Installation> = { name };
    if (description) data.description = description;
    if (companyId) data.company_id = companyId;
    if (brand) data.brand = brand;

    // Build nested location
    if (address || latitude || longitude) {
      data.location = {};
      if (address) data.location.address = address;
      if (latitude) data.location.latitude = parseFloat(latitude);
      if (longitude) data.location.longitude = parseFloat(longitude);
    }

    // Build nested configuration — merge with existing to avoid wiping unedited fields.
    // Color-scale fields treat empty as explicit null so admins can clear the override
    // from the UI; the existing configuration already informs the "had any value" check.
    const hadColorScaleBefore =
      initialData?.configuration?.color_scale_min != null ||
      initialData?.configuration?.color_scale_max != null;
    const hasAnyConfig =
      leakageThreshold ||
      deadmanValue ||
      gridStep ||
      analyticsHistoryDays ||
      influxInstallationName ||
      defaultReferenceElectrode ||
      voltageComboFilter ||
      colorScaleMin ||
      colorScaleMax ||
      hadColorScaleBefore;
    if (hasAnyConfig) {
      data.configuration = { ...(initialData?.configuration ?? {}) };
      if (leakageThreshold) data.configuration.leakage_threshold = parseFloat(leakageThreshold);
      // Convert value + unit back to the stored unit: integer hours.
      if (deadmanValue) {
        const v = parseFloat(deadmanValue);
        if (!isNaN(v)) {
          data.configuration.deadman_threshold = Math.round(deadmanUnit === 'days' ? v * 24 : v);
        }
      }
      if (gridStep) data.configuration.grid_step = parseFloat(gridStep);
      if (analyticsHistoryDays)
        data.configuration.analytics_history_days = parseInt(analyticsHistoryDays);
      data.configuration.influx_installation_name = influxInstallationName || undefined;
      data.configuration.default_reference_electrode = defaultReferenceElectrode || undefined;
      data.configuration.voltage_combo_filter = voltageComboFilter || undefined;
      // Color-scale override: empty string → null (clears); otherwise parse as float.
      const csMin = parseFloat(colorScaleMin);
      data.configuration.color_scale_min =
        colorScaleMin === '' ? null : isNaN(csMin) ? null : csMin;
      const csMax = parseFloat(colorScaleMax);
      data.configuration.color_scale_max =
        colorScaleMax === '' ? null : isNaN(csMax) ? null : csMax;
    }

    // Build nested metadata
    if (monitoredArea || dateStartOperation || measuringSystem) {
      data.installation_metadata = {};
      if (monitoredArea) data.installation_metadata.monitored_area = parseFloat(monitoredArea);
      if (dateStartOperation) data.installation_metadata.date_start_operation = dateStartOperation;
      if (measuringSystem) data.installation_metadata.measuring_system = measuringSystem;
    }

    onSubmit(data);
  };

  return (
    <form className="management-form" onSubmit={handleSubmit}>
      {/* Basic Info */}
      <div className="management-form-field">
        <label htmlFor="inst-name">{t('management.installation.name')} *</label>
        <input
          id="inst-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="inst-description">{t('management.installation.description')}</label>
        <textarea
          id="inst-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="inst-company">{t('management.installation.company')}</label>
        <select id="inst-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
          <option value="">{t('management.project.selectCompany')}</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Location Section */}
      <div className="management-form-section">
        <h4 className="management-form-section-title">{t('management.installation.location')}</h4>
        <div className="management-form-field">
          <label htmlFor="inst-address">{t('management.installation.address')}</label>
          <input
            id="inst-address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>
        <div className="management-form-row">
          <div className="management-form-field">
            <label htmlFor="inst-lat">{t('management.installation.latitude')}</label>
            <input
              id="inst-lat"
              type="number"
              step="any"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
            />
          </div>
          <div className="management-form-field">
            <label htmlFor="inst-lon">{t('management.installation.longitude')}</label>
            <input
              id="inst-lon"
              type="number"
              step="any"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Configuration Section */}
      <div className="management-form-section">
        <h4 className="management-form-section-title">
          {t('management.installation.configuration')}
        </h4>
        <div className="management-form-row">
          <div className="management-form-field">
            <label htmlFor="inst-leakage">{t('management.installation.leakageThreshold')}</label>
            <input
              id="inst-leakage"
              type="number"
              step="any"
              value={leakageThreshold}
              onChange={(e) => setLeakageThreshold(e.target.value)}
            />
          </div>
          <div className="management-form-field">
            <label htmlFor="inst-deadman">{t('management.installation.deadmanThreshold')}</label>
            <div className="management-form-inline">
              <input
                id="inst-deadman"
                type="number"
                step="any"
                min="0"
                value={deadmanValue}
                onChange={(e) => setDeadmanValue(e.target.value)}
              />
              <select
                aria-label={t('management.installation.deadmanUnit')}
                value={deadmanUnit}
                onChange={(e) => setDeadmanUnit(e.target.value as 'hours' | 'days')}
              >
                <option value="hours">{t('management.installation.unitHours')}</option>
                <option value="days">{t('management.installation.unitDays')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Configuration Section */}
      <div className="management-form-section">
        <h4 className="management-form-section-title">
          {t('management.installation.pipelineConfig')}
        </h4>
        <div className="management-form-row">
          <div className="management-form-field">
            <label htmlFor="inst-grid-step">{t('management.installation.gridStep')}</label>
            <input
              id="inst-grid-step"
              type="number"
              step="0.1"
              min="0.1"
              placeholder="1.0"
              value={gridStep}
              onChange={(e) => setGridStep(e.target.value)}
            />
          </div>
          <div className="management-form-field">
            <label htmlFor="inst-history-days">
              {t('management.installation.analyticsHistoryDays')}
            </label>
            <input
              id="inst-history-days"
              type="number"
              step="1"
              min="1"
              placeholder="1"
              value={analyticsHistoryDays}
              onChange={(e) => setAnalyticsHistoryDays(e.target.value)}
            />
          </div>
        </div>
        <div className="management-form-field">
          <label htmlFor="inst-influx-name">
            {t('management.installation.influxInstallationName')}
          </label>
          <input
            id="inst-influx-name"
            type="text"
            placeholder={name || t('management.installation.influxInstallationNameHint')}
            value={influxInstallationName}
            onChange={(e) => setInfluxInstallationName(e.target.value)}
          />
        </div>
        <div className="management-form-field">
          <label htmlFor="inst-default-electrode">
            {t('management.installation.defaultReferenceElectrode')}
          </label>
          <input
            id="inst-default-electrode"
            type="text"
            placeholder="e.g. signal_electrode_SE1"
            value={defaultReferenceElectrode}
            onChange={(e) => setDefaultReferenceElectrode(e.target.value)}
          />
        </div>
        <div className="management-form-field">
          <label htmlFor="inst-voltage-filter">
            {t('management.installation.voltageComboFilter')}
          </label>
          <input
            id="inst-voltage-filter"
            type="text"
            placeholder="e.g. (+24V) - (-24V)|signal_electrode_SE1"
            value={voltageComboFilter}
            onChange={(e) => setVoltageComboFilter(e.target.value)}
          />
        </div>
        <div className="management-form-row">
          <div className="management-form-field">
            <label htmlFor="inst-color-min">{t('management.installation.colorScaleMin')}</label>
            <input
              id="inst-color-min"
              type="number"
              step="any"
              placeholder={t('management.installation.colorScaleAutoHint')}
              value={colorScaleMin}
              onChange={(e) => setColorScaleMin(e.target.value)}
            />
          </div>
          <div className="management-form-field">
            <label htmlFor="inst-color-max">{t('management.installation.colorScaleMax')}</label>
            <input
              id="inst-color-max"
              type="number"
              step="any"
              placeholder={t('management.installation.colorScaleAutoHint')}
              value={colorScaleMax}
              onChange={(e) => setColorScaleMax(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Metadata Section */}
      <div className="management-form-section">
        <h4 className="management-form-section-title">{t('management.installation.metadata')}</h4>
        <div className="management-form-row">
          <div className="management-form-field">
            <label htmlFor="inst-area">{t('management.installation.monitoredArea')}</label>
            <input
              id="inst-area"
              type="number"
              step="any"
              value={monitoredArea}
              onChange={(e) => setMonitoredArea(e.target.value)}
            />
          </div>
          <div className="management-form-field">
            <label htmlFor="inst-date">{t('management.installation.dateStartOperation')}</label>
            <input
              id="inst-date"
              type="date"
              value={dateStartOperation}
              onChange={(e) => setDateStartOperation(e.target.value)}
            />
          </div>
        </div>
        <div className="management-form-field">
          <label htmlFor="inst-system">{t('management.installation.measuringSystem')}</label>
          <input
            id="inst-system"
            type="text"
            value={measuringSystem}
            onChange={(e) => setMeasuringSystem(e.target.value)}
          />
        </div>
        <div className="management-form-field">
          <label htmlFor="inst-brand">{t('management.installation.brand')}</label>
          <select id="inst-brand" value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="protectum">Protectum</option>
            <option value="eld">ELD</option>
          </select>
        </div>
      </div>

      <div className="entity-modal-footer" style={{ padding: 0, border: 'none' }}>
        <button
          type="button"
          className="management-btn management-btn--secondary"
          onClick={onCancel}
        >
          {t('management.cancel')}
        </button>
        <button
          type="submit"
          className="management-btn management-btn--primary"
          disabled={isLoading}
        >
          {isLoading ? t('management.saving') : t('management.save')}
        </button>
      </div>
    </form>
  );
};

export default InstallationForm;
